import type { ClinicalDocumentData } from "@qendo/profile/files";
import { Stack } from "expo-router";
import { useState } from "react";
import { Button } from "~/components/Button";
import { FilteredList } from "~/components/layout/FilteredList";
import { ListingItem } from "~/components/layout/ListingItem";
import { Page } from "~/components/layout/Page";
import { SectionHeader } from "~/components/layout/SectionHeader";
import { Authorize } from "~/components/permission/Authorize";
import { Can } from "~/components/permission/Can";
import { ShareButton } from "~/components/ShareButton";
import { useGallery } from "~/providers/GalleryProvider";
import { filled } from "~/util";
import { plural, searchMatches } from "~/util/string";

export default function DocumentsIndex() {
    const { documents, refreshDocuments } = useGallery();
    const [refreshing, setRefreshing] = useState(false);

    const refresh = async () => {
        if (refreshing) {
            return;
        }

        setRefreshing(true);

        await refreshDocuments().finally(() => setRefreshing(false));
    };

    const has_items = filled(documents);

    return (
        <Authorize ability="view" section="documents">
            <Stack.Screen
                options={{
                    headerRight: () => <ShareButton title="Share documents" initial={["documents"]} />,
                }}
            />
            <Page spacer refreshWith={refresh} isRefreshing={refreshing} scrollable={!has_items}>
                <SectionHeader
                    title="Clinical documents"
                    icon="file"
                    button={() => (
                        <Can ability="update" section="documents">
                            <Button
                                title="Add Document"
                                variant="primary"
                                size="lg"
                                href="/health-profile/clinical-documents/create"
                            />
                        </Can>
                    )}
                >
                    Store medical documents here, like test results, scans, or referrals, to keep them organised and
                    accessible.
                </SectionHeader>
                {filled(documents) && (
                    <FilteredList<ClinicalDocumentData>
                        data={documents}
                        searchWith={(item, term) => searchMatches(term, item.search)}
                        searchPlaceholder="Search by name"
                        refreshWith={refresh}
                        isRefreshing={refreshing}
                        renderItem={({ item }) => (
                            <ListingItem
                                title={item.name}
                                hideChevron
                                badge={item.pain_plan ? "Prescribed pain plan" : null}
                                badgeVariant="info"
                                href={{
                                    pathname: `/health-profile/clinical-documents/[id]/show`,
                                    params: { id: item.id },
                                }}
                                subtitle={filled(item.files) ? `${plural("File", item.files)} attached` : undefined}
                                last
                            />
                        )}
                    />
                )}
            </Page>
        </Authorize>
    );
}
